import { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";

interface PropertyFiltersProps {
	onFiltersChange: (filters: {
		search?: string;
		status?: string;
		property_type?: string;
		city?: string;
		state?: string;
	}) => void;
	onClearFilters: () => void;
}

const PropertyFilters: React.FC<PropertyFiltersProps> = ({
	onFiltersChange,
	onClearFilters,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [filters, setFilters] = useState({
		search: "",
		city: "",
		state: "",
		property_type: "",
		status: "",
	});

	const propertyTypeOptions = [
		{ value: "", label: "All Types" },
		{ value: "apartment", label: "Apartment" },
		{ value: "house", label: "House" },
		{ value: "condo", label: "Condo" },
		{ value: "commercial", label: "Commercial" },
		{ value: "office", label: "Office" },
		{ value: "other", label: "Other" },
	];

	const statusOptions = [
		{ value: "", label: "All Status" },
		{ value: "available", label: "Available" },
		{ value: "pending", label: "Pending Deal" },
		{ value: "leased", label: "Leased" },
		{ value: "sold", label: "Sold" },
		{ value: "maintenance", label: "Maintenance" },
	];

	const handleFilterChange = (key: string, value: string) => {
		const newFilters = { ...filters, [key]: value };
		setFilters(newFilters);
		onFiltersChange(newFilters);
	};

	const handleClearFilters = () => {
		const clearedFilters = {
			search: "",
			city: "",
			state: "",
			property_type: "",
			status: "",
		};
		setFilters(clearedFilters);
		onClearFilters();
	};

	const hasActiveFilters = Object.values(filters).some((value) => value !== "");

	return (
		<div className="bg-card rounded-lg border border-border p-4 mb-6">
			{/* Search and Toggle */}
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
				<div className="flex-1 max-w-md">
					<Input
						type="search"
						placeholder="Search properties by address, city, or ID..."
						value={filters.search}
						onChange={(e) => handleFilterChange("search", e.target.value)}
						className="w-full"
					/>
				</div>

				<div className="flex items-center space-x-2">
					{hasActiveFilters && (
						<Button
							variant="outline"
							size="sm"
							onClick={handleClearFilters}
							iconName="X"
							iconPosition="left"
						>
							Clear Filters
						</Button>
					)}

					<Button
						variant="outline"
						size="sm"
						onClick={() => setIsExpanded(!isExpanded)}
						iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
						iconPosition="right"
						className="lg:hidden"
					>
						Advanced Filters
					</Button>
				</div>
			</div>

			{/* Advanced Filters */}
			<div className={`${isExpanded ? "block" : "hidden"} lg:block`}>
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
					<Input
						label="City"
						type="text"
						placeholder="Filter by city"
						value={filters.city}
						onChange={(e) => handleFilterChange("city", e.target.value)}
					/>

					<Input
						label="State"
						type="text"
						placeholder="Filter by state"
						value={filters.state}
						onChange={(e) => handleFilterChange("state", e.target.value)}
					/>

					<Select
						label="Property Type"
						options={propertyTypeOptions}
						value={filters.property_type}
						onValueChange={(value) =>
							handleFilterChange("property_type", value as string)
						}
						placeholder="Select type"
					/>

					<Select
						label="Status"
						options={statusOptions}
						value={filters.status}
						onValueChange={(value) =>
							handleFilterChange("status", value as string)
						}
						placeholder="Select status"
					/>
				</div>

				{/* Filter Summary */}
				{hasActiveFilters && (
					<div className="mt-4 pt-4 border-t border-border">
						<div className="flex flex-wrap items-center gap-2">
							<span className="text-sm text-muted-foreground">
								Active filters:
							</span>
							{Object.entries(filters).map(([key, value]) => {
								if (!value) return null;

								let displayValue = value;
								if (key === "property_type") {
									displayValue =
										propertyTypeOptions.find((opt) => opt.value === value)
											?.label || value;
								} else if (key === "status") {
									displayValue =
										statusOptions.find((opt) => opt.value === value)?.label ||
										value;
								}

								return (
									<span
										key={key}
										className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
									>
										{displayValue}
										<button
											onClick={() => handleFilterChange(key, "")}
											className="ml-1 hover:text-primary/80"
										>
											<Icon name="X" size={12} />
										</button>
									</span>
								);
							})}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default PropertyFilters;
